/* eslint-disable complexity */
import React, { useCallback, useState, useEffect } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import {
  OnboardingFlowNode,
  OnboardingFlowStepNode,
  OnboardingFlowNodeType,
  OnboardingStepType,
  OnboardingStepAttributeKey,
  getOnboardingStepAttributeMap,
} from '@scale/shared/onboardingControlPanel';
import { useOCPStore } from '../hooks/useOCPStore';
import { useDraftFlowStore } from '../hooks/useDraftFlowStore';
import { Chip, Icon, Tooltip, Typography } from '@scale/scaleui';
import { isNodeUneditable, isTerminusNode } from '../config/config';
import { twMerge } from 'tailwind-merge';
import {
  useOCPStatsStepActiveUsers,
  useOCPStatsStepConversionRate,
  useOCPStatsStepAverageTimeSpent,
} from '../api';

const FlowNode = ({ id, data: node }: NodeProps<OnboardingFlowNode>) => {
  const {
    detailPanelOpen,
    toggleDetailPanel,
    detailTarget,
    setDetailTarget,
    currentFlow,
    currentVersion,
    validationResults,
    userTraversal,
    flowFilterDates,
    flowFilterTags,
    canFreeEdit,
  } = useOCPStore(state => ({
    detailPanelOpen: state.detailPanelOpen,
    toggleDetailPanel: state.toggleDetailPanel,
    detailTarget: state.detailTarget,
    setDetailTarget: state.setDetailTarget,
    currentFlow: state.currentFlow,
    currentVersion: state.currentVersion,
    validationResults: state.validationResults,
    userTraversal: state.userTraversal,
    flowFilterDates: state.flowFilterDates,
    flowFilterTags: state.flowFilterTags,
    canFreeEdit: state.canFreeEdit,
  }));
  const { addNode } = useDraftFlowStore(state => ({
    addNode: state.addNode,
  }));
  const uneditable =
    node.name === 'Signup' ||
    isNodeUneditable(node as OnboardingFlowNode, canFreeEdit ? currentFlow : null);

  const [currentStep, setCurrentStep] = useState(false);
  const [completedStep, setCompletedStep] = useState(false);
  const [bypassedStep, setBypassedStep] = useState(false);
  const [unvisitableStep, setUnvisitableStep] = useState(false);

  const [bgColorClass, setBgColorClass] = useState('bg-[#fff]');
  const [borderColor, setBorderColor] = useState('border-neutral-200');
  const [opacity, setOpacity] = useState('opacity-100');

  const handleNodeClick = useCallback(() => {
    if (!detailPanelOpen) toggleDetailPanel();
    setDetailTarget(node);
  }, [detailPanelOpen, toggleDetailPanel, setDetailTarget, node]);

  const handleAddNode = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      if (currentFlow) addNode(currentFlow._id, node);
    },
    [node, addNode, currentFlow],
  );
  const selected = (detailTarget as OnboardingFlowNode)?.name === node.name;
  const canConnectAsDestination = !uneditable && currentVersion === -1;
  // TODO: refactor so that we are comparing to the last platform onboarding step, not the hardcoded JoinSlack step
  const canConnectAsSource =
    (!uneditable ||
      (node as OnboardingFlowStepNode)?.stepType === OnboardingStepType.JoinSlack ||
      (node as OnboardingFlowStepNode)?.stepType === OnboardingStepType.JoinCommunity) &&
    node?.type !== OnboardingFlowNodeType.Terminus &&
    currentVersion === -1;

  const tagIds = flowFilterTags.map(tag => tag.value);
  const { isLoading: isLoadingActiveUsers, data: activeUserData } = useOCPStatsStepActiveUsers({
    flowId: currentFlow?._id ? (!currentFlow._id.startsWith('tempId_') ? currentFlow._id : '') : '',
    step: node.name,
    tags: tagIds,
  });
  const { isLoading: isLoadingConversionRate, data: conversionRateData } =
    useOCPStatsStepConversionRate({
      flowId: currentFlow?._id
        ? !currentFlow._id.startsWith('tempId_')
          ? currentFlow._id
          : ''
        : '',
      step: node.name,
      flowVersion: currentVersion,
      fromTime: flowFilterDates[0],
      toTime: flowFilterDates[1],
      tags: tagIds,
    });
  const { isLoading: isLoadingAverageTimeSpent, data: averageTimeSpentData } =
    useOCPStatsStepAverageTimeSpent({
      flowId: currentFlow?._id
        ? !currentFlow._id.startsWith('tempId_')
          ? currentFlow._id
          : ''
        : '',
      step: node.name,
      flowVersion: currentVersion,
      fromTime: flowFilterDates[0],
      toTime: flowFilterDates[1],
      tags: tagIds,
    });
  const nodeIconMap = getOnboardingStepAttributeMap(OnboardingStepAttributeKey.IconName);
  const validationErrors = validationResults.errors.filter(error =>
    error.message.includes(node.name),
  );

  // TODO: clean these useEffects up
  useEffect(() => {
    if (userTraversal && node) {
      setCurrentStep(userTraversal.currentStep === node.name);
      setCompletedStep(userTraversal.completedSteps.includes(node.name));
      setBypassedStep(userTraversal.bypassedSteps.includes(node.name));
      setUnvisitableStep(
        userTraversal.unvisitableSteps.includes(node.name) &&
          !userTraversal.completedSteps.includes(node.name),
      );
    } else {
      setCurrentStep(false);
      setCompletedStep(false);
      setBypassedStep(false);
      setUnvisitableStep(false);
    }
  }, [userTraversal, node]);

  useEffect(() => {
    if (currentStep) {
      setBgColorClass('bg-primary-100');
    } else if (completedStep) {
      setBgColorClass('bg-primary-50');
    } else if (unvisitableStep) {
      setBgColorClass('bg-neutral-300');
    } else if (uneditable) {
      setBgColorClass('bg-neutral-200');
    } else {
      setBgColorClass('bg-[#fff]');
    }
  }, [selected, uneditable, currentStep, completedStep, bypassedStep, unvisitableStep]);

  useEffect(() => {
    if (unvisitableStep) {
      setOpacity('opacity-50');
    } else if (bypassedStep) {
      setOpacity('opacity-65');
    } else {
      setOpacity('opacity-100');
    }
  }, [bypassedStep, unvisitableStep]);

  useEffect(() => {
    if (selected || completedStep || bypassedStep || currentStep) {
      setBorderColor('border-[#6A5DF9]');
    } else if (validationErrors.length) {
      setBorderColor('border-[#CC3333]');
    } else {
      setBorderColor('border-neutral-200');
    }
  }, [selected, completedStep, bypassedStep, currentStep, validationErrors]);

  const isNodeLoadBalancer = (node: OnboardingFlowNode) => {
    if (
      node.type === OnboardingFlowNodeType.Step &&
      node.stepType === OnboardingStepType.LoadBalancer
    ) {
      return true;
    }
    return false;
  };

  return (
    <>
      {/* Top handle for incoming edges */}
      <Handle
        type="target"
        position={Position.Left}
        id={`${id}_target`}
        style={{ borderRadius: '50%', backgroundColor: '#6B7280', borderColor: '#6B7280' }}
        isConnectableStart={false}
        isConnectable={canConnectAsDestination}
      />

      {/* Custom node content */}
      <div
        className={twMerge(
          'border rounded min-w-[200px] max-w-[260px] relative',
          borderColor,
          bgColorClass,
          opacity,
        )}
        onClick={handleNodeClick}
      >
        <div className="flex items-center p-2">
          <Icon
            variant={
              node.type === OnboardingFlowNodeType.Terminus
                ? node.success
                  ? 'success'
                  : 'danger'
                : 'neutral'
            }
            icon={[
              'far',
              nodeIconMap[(node as OnboardingFlowStepNode)?.stepType ?? node.type] as IconName,
            ]}
          />
          <Typography.Paragraph2 className="text-sm font-medium text-[#374151]">
            {node.name}
          </Typography.Paragraph2>
          <div className="flex items-center ml-auto">
            {bypassedStep && (
              <>
                &nbsp;&nbsp;
                <p style={{ color: '#6A5DF9' }}>
                  <i>Bypassed</i>
                </p>
              </>
            )}
            {node.emailCampaignId && <Icon variant="neutral" icon="envelope" />}
            {uneditable && <Icon variant="neutral" icon="lock" />}
          </div>
        </div>
        <div
          id="divider"
          className="h-0 w-[calc(100% - 16px)] mx-2 border-b border-b-neutral-200"
        />
        <div className="flex gap-2 p-2">
          <Chip
            label={isLoadingActiveUsers ? '...' : activeUserData?.result ?? 'N/A'}
            leadingIcon="user"
          />
          {!isTerminusNode(node) && (
            <Chip
              label={
                isLoadingAverageTimeSpent
                  ? '...'
                  : averageTimeSpentData?.result
                  ? `${Math.round(averageTimeSpentData?.result)} min`
                  : 'N/A'
              }
              leadingIcon="clock"
            />
          )}
          {!isTerminusNode(node) && (
            <Chip
              label={
                isLoadingConversionRate
                  ? '...'
                  : conversionRateData?.result
                  ? `${Math.round(conversionRateData?.result * 100)}%`
                  : 'N/A'
              }
              leadingIcon="funnel-dollar"
            />
          )}
        </div>
        {canConnectAsSource && (
          <div className="absolute top-0" style={{ right: -26 }}>
            <Icon
              icon={['far', 'plus-circle']}
              variant="primary"
              className="!bg-transparent !hover:bg-transparent far"
              onClick={handleAddNode}
              tooltip="Add Node and Transition"
              disabled={isNodeLoadBalancer(node)}
            />
          </div>
        )}
      </div>

      {/* Bottom handle for outgoing edges */}
      <Tooltip
        text="Add Transition"
        target={
          <Handle
            type="source"
            position={Position.Right}
            id={`${id}_source`}
            style={{
              borderRadius: '50%',
              backgroundColor:
                canConnectAsSource || isTerminusNode(node) ? 'transparent' : '#6B7280',
              borderColor: canConnectAsSource || isTerminusNode(node) ? 'transparent' : '#6B7280',
              margin: canConnectAsSource ? '0 -10px 0 0' : 0,
              width: canConnectAsSource ? '14px' : '6px',
              height: canConnectAsSource ? '14px' : '6px',
            }}
            isConnectableEnd={false}
            isConnectable={canConnectAsSource}
          >
            {canConnectAsSource && (
              <Icon
                icon={['far', 'arrow-alt-circle-right']}
                variant="primary"
                className="!bg-transparent !hover:bg-transparent pointer-events-none"
                disabled={isNodeLoadBalancer(node)}
              />
            )}
          </Handle>
        }
      />
    </>
  );
};

export default FlowNode;
